// src/App.jsx — v4c
import React, { useEffect } from "react";
import {
  BrowserRouter, Routes, Route, Navigate,
  Link, useNavigate, useLocation,
} from "react-router-dom";
import {
  ThemeProvider, CssBaseline, AppBar, Toolbar, Button, Container,
  Avatar, Menu, MenuItem, Divider, IconButton, Box, Chip, Stack,
  ListItemIcon, Typography, alpha, useMediaQuery, Drawer, List,
  ListItem, ListItemButton, ListItemText,
} from "@mui/material";
import LogoutIcon             from "@mui/icons-material/Logout";
import SchoolIcon             from "@mui/icons-material/School";
import DashboardIcon          from "@mui/icons-material/Dashboard";
import BarChartIcon           from "@mui/icons-material/BarChart";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AutoStoriesIcon        from "@mui/icons-material/AutoStories";
import MenuIcon               from "@mui/icons-material/Menu";

import { theme, C, indigoGradient } from "./theme";
import { ToastProvider }  from "./context/ToastContext";
import { P }              from "./theme";
import { isAuthenticated, isAdmin, clearToken, bootstrapUserInfo } from "./auth";

import LanguageSwitcher from "./components/LanguageSwitcher";
import Home             from "./pages/Home";
import Login            from "./pages/Login";
import Register         from "./pages/Register";
import ExamsList        from "./pages/ExamsList";
import ExamPass         from "./pages/ExamPass";
import Results          from "./pages/Results";
import StudentDashboard from "./pages/StudentDashboard";
import AdminDashboard   from "./pages/AdminDashboard";
import NotFound         from "./pages/NotFound";
import ForgotPassword  from "./pages/ForgotPassword";
import SSOCallback     from "./pages/SSOCallback";
import ResetPassword   from "./pages/ResetPassword";
import AdminLayout      from "./pages/admin/AdminLayout";
import AdminExams       from "./pages/admin/AdminExams";
import AdminQuestions   from "./pages/admin/AdminQuestions";
import AdminUsers       from "./pages/admin/AdminUsers";
import AdminImport      from "./pages/admin/AdminImport";
import AdminResults     from "./pages/admin/AdminResults";
import AdminAnalytics   from "./pages/admin/AdminAnalytics";
import AdminAuditLogs   from "./pages/admin/AdminAuditLogs";
import AdminBackups     from "./pages/admin/AdminBackups";

// ── Liens de navigation avec indicateur actif ─────────────────────────────
function NavLink({ to, label, icon, exact }) {
  const { pathname } = useLocation();
  const active = exact ? pathname === to : (to !== "/" && pathname.startsWith(to));
  return (
    <Button
      component={Link} to={to}
      startIcon={icon}
      sx={{
        fontSize: "0.85rem",
        fontWeight: active ? 700 : 500,
        px: 1.5, py: 0.75,
        textTransform: "none",
        color: active ? "white" : alpha("#fff", 0.65),
        background: active ? alpha("#fff", 0.12) : "transparent",
        borderRadius: "8px",
        "&:hover": { color: "white", background: alpha("#fff", 0.1) },
      }}
    >
      {label}
    </Button>
  );
}

// ── Barre de navigation ────────────────────────────────────────────────────
function NavBar() {
  const navigate    = useNavigate();
  const isMobile    = useMediaQuery("(max-width:768px)");
  const [, setTick] = React.useState(0);
  const [anchor,    setAnchor]  = React.useState(null);
  const [drawerOpen, setDrawer] = React.useState(false);

  // Ré-render au changement d'auth (login / logout)
  React.useEffect(() => {
    const fn = () => setTick(t => t + 1);
    window.addEventListener("auth-changed", fn);
    return () => window.removeEventListener("auth-changed", fn);
  }, []);

  const auth  = isAuthenticated();
  const admin = isAdmin();
  const userInfo = (() => {
    try { return JSON.parse(localStorage.getItem("user_info") || "{}"); }
    catch { return {}; }
  })();
  const initials = (userInfo.full_name || userInfo.username || "U")
    .split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const onLogout = () => {
    clearToken();
    setAnchor(null);
    setDrawer(false);
    window.dispatchEvent(new Event("auth-changed"));
    navigate("/login");
  };

  // Liens selon le rôle
  const studentLinks = [
    { to: "/exams",     label: "Examens",      icon: <SchoolIcon     fontSize="small" /> },
    { to: "/dashboard", label: "Mon espace",   icon: <DashboardIcon  fontSize="small" /> },
    { to: "/results",   label: "Résultats",    icon: <BarChartIcon   fontSize="small" /> },
  ];
  const adminLinks = [
    { to: "/admin",            label: "Dashboard",      icon: <DashboardIcon          fontSize="small" /> },
    { to: "/admin/exams",      label: "Examens",        icon: <SchoolIcon             fontSize="small" /> },
    { to: "/admin/questions",  label: "Questions",      icon: <SchoolIcon             fontSize="small" /> },
    { to: "/admin/users",      label: "Utilisateurs",   icon: <AdminPanelSettingsIcon fontSize="small" /> },
    { to: "/admin/results",    label: "Résultats",      icon: <BarChartIcon           fontSize="small" /> },
    { to: "/admin/import",     label: "Import",         icon: <SchoolIcon             fontSize="small" /> },
  ];
  const links = admin ? adminLinks : studentLinks;

  return (
    <>
      <AppBar position="sticky" elevation={0}
        sx={{ background: P.navy, borderBottom: `1px solid ${alpha("#fff", 0.08)}` }}>
        <Toolbar sx={{ minHeight: "60px !important", gap: 0.5 }}>
          {/* Logo */}
          <Box component={Link} to={auth ? (admin ? "/admin" : "/dashboard") : "/"}
            sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none", mr: 2, flexShrink: 0 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: indigoGradient,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AutoStoriesIcon sx={{ color: "white", fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", letterSpacing: "-0.01em", color: "white" }}>
              ExamOnline
            </Typography>
          </Box>

          {/* Liens desktop */}
          {auth && (
            <Stack direction="row" spacing={0.25} sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
              {links.map(l => <NavLink key={l.to} {...l} />)}
            </Stack>
          )}

          <Box sx={{ flexGrow: 1, display: { md: "none" } }} />

          {/* Partie droite */}
          {auth ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {admin && (
                <Chip label="Admin" size="small" sx={{
                  background: alpha("#f59e0b", .2), color: "#fcd34d",
                  fontWeight: 700, fontSize: "0.68rem",
                  display: { xs: "none", sm: "flex" },
                }} />
              )}
              {/* Avatar + menu desktop */}
              <IconButton onClick={e => setAnchor(e.currentTarget)} size="small"
                sx={{ display: { xs: "none", sm: "flex" } }}>
                <Avatar sx={{ width: 32, height: 32, background: alpha("#fff", .15),
                  fontSize: "0.8rem", fontWeight: 700 }}>
                  {initials}
                </Avatar>
              </IconButton>
              <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{ sx: { mt: 1, borderRadius: 2, minWidth: 180 } }}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {userInfo.full_name || userInfo.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {admin ? "Administrateur" : "Étudiant"}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={onLogout} sx={{ gap: 1.5, color: P.red, fontWeight: 600 }}>
                  <LogoutIcon fontSize="small" />
                  Déconnexion
                </MenuItem>
              </Menu>

              {/* Burger mobile */}
              <IconButton onClick={() => setDrawer(true)}
                sx={{ color: "white", display: { md: "none" } }}>
                <MenuIcon />
              </IconButton>
            </Stack>
          ) : (
            <Button component={Link} to="/login" variant="outlined"
              sx={{ color: "white", borderColor: alpha("#fff", .4), textTransform: "none",
                fontWeight: 700, borderRadius: "8px",
                "&:hover": { borderColor: "white", background: alpha("#fff", .08) } }}>
              Connexion
            </Button>
          )}
          <LanguageSwitcher sx={{ color: "white" }} />
        </Toolbar>
      </AppBar>

      {/* Drawer mobile */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawer(false)}
        PaperProps={{ sx: { width: 260, background: P.navy } }}>
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ color: "white", fontWeight: 800, fontSize: "1rem" }}>ExamOnline</Typography>
          <Typography sx={{ color: alpha("#fff", .5), fontSize: "0.8rem", mt: .25 }}>
            {userInfo.username}{admin ? " · Admin" : ""}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: alpha("#fff", .1) }} />
        <List sx={{ px: 1, py: 1 }}>
          {links.map(l => {
            const { pathname } = window.location;
            const active = pathname.startsWith(l.to);
            return (
              <ListItem key={l.to} disablePadding sx={{ mb: .25 }}>
                <ListItemButton component={Link} to={l.to} onClick={() => setDrawer(false)}
                  sx={{ borderRadius: "8px",
                    background: active ? alpha("#fff", .12) : "transparent",
                    "&:hover": { background: alpha("#fff", .08) } }}>
                  <ListItemText
                    primary={l.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 700 : 500,
                      color: active ? "white" : alpha("#fff", .7),
                      fontSize: "0.9rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider sx={{ borderColor: alpha("#fff", .1), mt: "auto" }} />
        <Box sx={{ p: 2 }}>
          <Button fullWidth onClick={onLogout} startIcon={<LogoutIcon />}
            sx={{ color: alpha("#fff", .7), textTransform: "none", justifyContent: "flex-start",
              "&:hover": { color: "white" } }}>
            Déconnexion
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

// ── Guards de route ────────────────────────────────────────────────────────
function PrivateRoute({ children }) {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
}
function AdminRoute({ children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!isAdmin())         return <Navigate to="/dashboard" replace />;
  return children;
}

// ── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  useEffect(() => {
    if (isAuthenticated()) bootstrapUserInfo().catch(() => {});
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <BrowserRouter>
          <NavBar />
          <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Routes>
              {/* ── Public ── */}
              <Route path="/"        element={<Home />} />
              <Route path="/login"   element={<Login />} />
              <Route path="/register"        element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password"  element={<ResetPassword />} />
              <Route path="/sso/callback"    element={<SSOCallback />} />

              {/* ── Étudiant ── */}
              <Route path="/dashboard" element={<PrivateRoute><StudentDashboard /></PrivateRoute>} />
              <Route path="/exams"     element={<PrivateRoute><ExamsList /></PrivateRoute>} />
              <Route path="/exams/:id" element={<PrivateRoute><ExamPass /></PrivateRoute>} />
              <Route path="/results"   element={<PrivateRoute><Results /></PrivateRoute>} />

              {/* ── Admin ── */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index           element={<AdminDashboard />} />
                <Route path="exams"    element={<AdminExams />} />
                <Route path="questions"element={<AdminQuestions />} />
                <Route path="users"    element={<AdminUsers />} />
                <Route path="results"   element={<AdminResults />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
                <Route path="backups"    element={<AdminBackups />} />
                <Route path="import"   element={<AdminImport />} />
              </Route>

              {/* ── 404 ── */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Container>
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  );
}